import { RiskBaseGlobal } from 'components/App/Global';
import { ReduxPersistStorage } from 'lib/redux-persist-storage';
import { combineReducers } from 'redux';
import { persistReducer } from 'redux-persist';

import { appReducer } from './app/reducer';
import { AppState } from './app/types';
import { asesReducer } from './ases/reducer';
import { AsesState } from './ases/types';
import { assessmentReducer } from './assessments/reducer';
import { assetReducer } from './assets/reducer';
import { AssetsState } from './assets/types';
import { authReducer } from './auth/reducer';
import { AuthState } from './auth/types';
import { changesReducer } from './changes/reducer';
import { errorReducer } from './error/reducer';
import { ErrorState } from './error/types';
import { filesReducer } from './files/reducer';
import { organisationReducer } from './organisations/reducer';
import { OrganisationsState } from './organisations/types';
import { planReducer } from './plan/reducer';
import { remoteReducer } from './remote/reducer';
import { search } from './search/reducer';
import { syncReducer } from './sync/reducer';
import { SyncState } from './sync/types';
import { RemoteState } from './remote/types';

const isDevPersist = false; //__DEV__ && true;

const typedWhitelist = <B, T extends B[] = B[]>(
  array: T,
  array2: T | null = null,
): T => (array2 ? ([...array, ...array2] as T) : array);

const persistReducerConfig = {
  storage: ReduxPersistStorage,
  writeFailHandler: (writeError: Error) => {
    RiskBaseGlobal.reduxPersistError = writeError;
  },
};

export const rootReducer = combineReducers({
  /**
   * App
   */
  app: persistReducer(
    {
      ...persistReducerConfig,
      key: 'app',
      whitelist: typedWhitelist<keyof AppState>([
        'navigation',
        'instances',
        'settingSavePhotosToMediaLibrary',
        'generic',
        'offlineOnly',
        'appType',
        'sidebar',
      ]),
    },
    appReducer,
  ),

  /**
   * Ases
   */
  ases: persistReducer(
    {
      ...persistReducerConfig,
      key: 'ases',
      whitelist: typedWhitelist<keyof AsesState>(
        ['listFilter', 'listFilterOptions', 'listData'],
        isDevPersist
          ? ['list', 'listLoadStatus', 'listOffline', 'listTotalCount']
          : null,
      ),
    },
    asesReducer,
  ),

  /**
   * Assessments
   */
  assessments: isDevPersist
    ? persistReducer(
        {
          ...persistReducerConfig,
          key: 'assessments',
        },
        assessmentReducer,
      )
    : assessmentReducer,

  /**
   * Assets
   */
  assets: persistReducer(
    {
      ...persistReducerConfig,
      key: 'assets',
      whitelist: typedWhitelist<keyof AssetsState>(
        ['assets_list', 'filter', 'sort', 'files'],
        isDevPersist ? ['loaded', 'loadedCount'] : null,
      ),
    },
    assetReducer,
  ),

  /**
   * Auth
   */
  auth: persistReducer(
    {
      ...persistReducerConfig,
      key: 'auth',
      whitelist: typedWhitelist<keyof AuthState>([
        'urlToken',
        'user',
        'organisations',
        'organisation',
      ]),
    },
    authReducer,
  ),

  /**
   * Changes
   */
  changes: persistReducer(
    {
      ...persistReducerConfig,
      key: 'changes',
    },
    changesReducer,
  ),

  /**
   * Errors
   */
  error: persistReducer(
    {
      ...persistReducerConfig,
      key: 'error',
      whitelist: typedWhitelist<keyof ErrorState>(['errors']),
    },
    errorReducer,
  ),

  /**
   * Organisations
   */
  organisations: persistReducer(
    {
      ...persistReducerConfig,
      key: 'organisations',
      whitelist: typedWhitelist<keyof OrganisationsState>(
        ['list', 'localMerge'],
        isDevPersist ? ['loaded', 'loadedCount'] : null,
      ),
    },
    organisationReducer,
  ),

  /**
   * Plan
   */
  plan: planReducer,

  /**
   * Remote
   */
  remote: persistReducer(
    {
      ...persistReducerConfig,
      key: 'remote',
      storage: ReduxPersistStorage,
      whitelist: isDevPersist
        ? undefined
        : typedWhitelist<keyof RemoteState>(['pagesSaved']),
    },
    remoteReducer,
  ),

  /**
   * Search
   */
  search,

  /**
   * Sync
   */
  sync: persistReducer(
    {
      ...persistReducerConfig,
      key: 'sync',
      whitelist: typedWhitelist<keyof SyncState>([
        'flagValueShouldDownloadCleanAssetData',
        'flagValueShouldDownloadCleanOrganisationData',
        'serverTimestamp',
        'asset_timestamps',
        'organisation_timestamps',
        'asset_removal_queue',
        'files_upload_queue',
        'downloadedFilesQueue',
        'downloadedFiles',
        'downloadedThumbnailsQueue',
        'downloadedThumbnails',
        'lastBgDataSyncTime',
        'nextAppUpdateCheckTime',
        'appUpdateAvailable',
      ]),
    },
    syncReducer,
  ),

  /**
   * Files
   */
  files: filesReducer,
});
